interface SecondaryAction {
  // Always rendered as an external `<a target="_blank">`. Internal
  // navigation should go through `onPrimaryAction` with `useNavigate`,
  // since rendering an internal href as a plain anchor would trigger a
  // full page reload and unmount the SPA boot.
  label: string;
  href: string;
  testId?: string;
}

interface ErrorStateProps {
  message: string;
  heading?: string;
  // Generic name on purpose: this is the primary action button, whether
  // the callback re-fetches (retry) or routes away (navigate). The
  // default label is still "Try again" to keep retry-style call sites
  // terse, but the contract is "do whatever the primary action is".
  onPrimaryAction?: () => void;
  primaryActionLabel?: string;
  primaryActionTestId?: string;
  secondaryAction?: SecondaryAction;
  testId?: string;
  className?: string;
}

const DEFAULT_CONTAINER =
  'flex flex-col items-center justify-center py-12 gap-4 text-center';

export function ErrorState({
  message,
  heading,
  onPrimaryAction,
  primaryActionLabel = 'Try again',
  primaryActionTestId,
  secondaryAction,
  testId,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={[DEFAULT_CONTAINER, className].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      {heading && (
        <h1 className="text-3xl font-bold text-wolisho-ink">{heading}</h1>
      )}
      <p
        role="alert"
        // Narrow the message column only on full-page error layouts
        // (where the heading is present). In-page error blurbs
        // (FeedList, AccountPage's edit form, etc.) keep the previous
        // full-width behaviour so this change doesn't silently re-flow
        // unrelated UI.
        className={['text-wolisho-red', heading ? 'max-w-md' : '']
          .filter(Boolean)
          .join(' ')}
      >
        {message}
      </p>
      {onPrimaryAction && (
        <button
          type="button"
          onClick={onPrimaryAction}
          data-testid={primaryActionTestId}
          className="px-4 py-2 bg-wolisho-red text-white rounded-lg hover:bg-wolisho-red/90 transition-colors"
        >
          {primaryActionLabel}
        </button>
      )}
      {secondaryAction && (
        <a
          href={secondaryAction.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={secondaryAction.testId}
          className="text-wolisho-red font-semibold text-sm hover:opacity-80 transition-opacity"
        >
          {secondaryAction.label}
        </a>
      )}
    </div>
  );
}
